/**
 * Explicit-content detection for lyrics / text — used to auto-apply the
 * Parental Advisory flag (`explicit`) on a work.
 *
 * Word-boundary matched so we don't false-positive on innocent substrings
 * ("class", "cocktail", "assume", "documented"…). Conservative English core
 * list; phrases are matched too. Entirely client-side (no cost, no API).
 */

// Core English profanity / sexual / explicit terms. Whole-word matched.
const EXPLICIT_TERMS: string[] = [
  "fuck", "fucks", "fucking", "fucked", "fucker", "fuckers", "motherfucker", "motherfuckers", "mothafucka", "muthafucka",
  "shit", "shits", "shitty", "bullshit", "bitch", "bitches", "bitchin",
  "cunt", "cunts", "cock", "cocks", "dick", "dicks", "pussy", "pussies",
  "asshole", "assholes", "nigga", "niggas", "nigger", "niggers",
  "whore", "whores", "slut", "sluts", "faggot", "faggots",
  "cum", "cumming", "jizz", "blowjob", "blowjobs", "handjob", "dildo", "gangbang",
  "rape", "rapist", "molest", "jerk off", "jack off", "wank", "twat", "prick",
  "goddamn", "bastard", "cocaine", "crackhead", "porn", "porno",
];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Pre-build one alternation with word-ish boundaries. \b doesn't play nice with
// leading/trailing apostrophes, so we bound on non-alphanumerics ourselves.
const PATTERN = new RegExp(
  `(^|[^a-z0-9])(${EXPLICIT_TERMS.map(escapeRe).join("|")})([^a-z0-9]|$)`,
  "gi"
);

export interface ExplicitScan {
  explicit: boolean;
  /** Distinct matched terms (lower-cased), for showing the creator what tripped it. */
  terms: string[];
}

export function detectExplicit(text: string | null | undefined): ExplicitScan {
  if (!text) return { explicit: false, terms: [] };
  const found = new Set<string>();
  const lower = text.toLowerCase();
  PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PATTERN.exec(lower)) !== null) {
    found.add(m[2]);
    // Avoid zero-width infinite loops if a boundary is empty.
    if (m.index === PATTERN.lastIndex) PATTERN.lastIndex++;
  }
  return { explicit: found.size > 0, terms: [...found] };
}
